import React, { useState, useEffect } from 'react';
import { Clock, X, Trash2 } from 'lucide-react';
import {
  getCalculationHistory,
  clearCalculationHistory,
  removeCalculationFromHistory,
  CalculationHistoryItem
} from '../utils/calculationHistory';

const RecentCalculations: React.FC = () => {
  const [history, setHistory] = useState<CalculationHistoryItem[]>([]);
  const [expanded, setExpanded] = useState<string | null>(null);

  const loadHistory = () => {
    setHistory(getCalculationHistory());
  };

  useEffect(() => {
    loadHistory();
    const interval = setInterval(loadHistory, 1000);
    return () => clearInterval(interval);
  }, []);

  const handleClear = () => {
    if (window.confirm('Clear all recent calculations?')) {
      clearCalculationHistory();
      loadHistory();
    }
  };

  const handleRemove = (id: string) => {
    removeCalculationFromHistory(id);
    loadHistory();
  };

  const formatTimestamp = (timestamp: number) => {
    const now = Date.now();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    const days = Math.floor(diff / 86400000);

    if (minutes < 1) return 'Just now';
    if (minutes < 60) return `${minutes}m ago`;
    if (hours < 24) return `${hours}h ago`;
    return `${days}d ago`;
  };

  if (history.length === 0) {
    return (
      <div className="calc-card">
        <div className="flex items-center gap-2 mb-4">
          <Clock className="w-5 h-5 text-safety-orange" />
          <h2 className="text-xl font-semibold">Recent Calculations</h2>
        </div>
        <p className="text-gray-500 dark:text-gray-400 text-sm">
          No recent calculations. Start using calculators to see your history here.
        </p>
      </div>
    );
  }

  return (
    <div className="calc-card">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-5 h-5 text-safety-orange" />
          <h2 className="text-xl font-semibold">Recent Calculations</h2>
        </div>
        <button
          onClick={handleClear}
          className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-md transition-colors"
          title="Clear all history"
        >
          <Trash2 className="w-4 h-4" />
        </button>
      </div>

      <div className="space-y-2">
        {history.map((item) => (
          <div
            key={item.id}
            className="border border-gray-200 dark:border-gray-700 rounded-md p-3 hover:border-safety-orange transition-colors"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="flex-1">
                <h3 className="font-medium text-sm">{item.calculatorName}</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400">
                  {formatTimestamp(item.timestamp)}
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setExpanded(expanded === item.id ? null : item.id)}
                  className="text-xs text-safety-orange hover:underline"
                >
                  {expanded === item.id ? 'Hide' : 'Show'}
                </button>
                <button
                  onClick={() => handleRemove(item.id)}
                  className="p-1 text-gray-400 hover:text-red-500 transition-colors"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
            </div>

            {expanded === item.id && (
              <div className="mt-3 pt-3 border-t border-gray-200 dark:border-gray-700 space-y-2">
                <div>
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Inputs:
                  </p>
                  <div className="text-xs space-y-1">
                    {Object.entries(item.inputs).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <p className="text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                    Results:
                  </p>
                  <div className="text-xs space-y-1">
                    {Object.entries(item.outputs).map(([key, value]) => (
                      <div key={key} className="flex justify-between">
                        <span className="text-gray-600 dark:text-gray-400">{key}:</span>
                        <span className="font-medium">{String(value)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default RecentCalculations;
